// Google Calendar v3: the events a triage view reads and stamps.
// https://developers.google.com/workspace/calendar/api/v3/reference/events

import type { GoogleApi } from "./client.js"

const BASE = "https://www.googleapis.com/calendar/v3/calendars"

export interface GCalEventTime {
  /** Set on an all-day event, which has no dateTime. */
  date?: string
  dateTime?: string
  timeZone?: string
}

export interface GCalAttendee {
  email?: string
  displayName?: string
  /** A room or other calendar resource, never a person. */
  resource?: boolean
  organizer?: boolean
  /** The calendar owner's own attendee. */
  self?: boolean
  optional?: boolean
  responseStatus?: string
}

export interface GCalEvent {
  id: string
  summary?: string
  description?: string
  location?: string
  htmlLink?: string
  status?: string
  start?: GCalEventTime
  end?: GCalEventTime
  recurrence?: string[]
  recurringEventId?: string
  attendees?: GCalAttendee[]
  extendedProperties?: {
    private?: Record<string, string>
    shared?: Record<string, string>
  }
}

/** A timed event: all-day events have a `date` and no `dateTime`. */
export type TimedEvent = GCalEvent & { start: GCalEventTime & { dateTime: string } }

export const isTimedEvent = (event: GCalEvent): event is TimedEvent => !!event.start?.dateTime

/** What an event patch may carry. In `extendedProperties.private`, null clears a
 *  key; ids go as numbers and flags as booleans (Google stores them as strings). */
export type GCalPatch = Omit<Partial<GCalEvent>, "extendedProperties"> & {
  extendedProperties?: {
    private?: Record<string, string | number | boolean | null>
  }
}

export interface ListEventsParams {
  /** Defaults to "primary". */
  calendarId?: string
  timeMin: string
  timeMax: string
  q?: string
  maxResults?: number
  maxAttendees?: number
}

export function createCalendar(api: GoogleApi) {
  const eventUrl = (eventId: string, calendarId: string) =>
    `${BASE}/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`

  return {
    events: {
      /** The calendar's events in the window, expanded to single instances and
       *  ordered by start. */
      list: async ({ calendarId = "primary", ...params }: ListEventsParams) => {
        const { items } = await api.get<{ items?: GCalEvent[] }>(
          `${BASE}/${encodeURIComponent(calendarId)}/events`,
          { ...params, singleEvents: true, orderBy: "startTime" }
        )
        return items ?? []
      },

      get: (eventId: string, calendarId = "primary") =>
        api.get<GCalEvent>(eventUrl(eventId, calendarId)),

      patch: (eventId: string, patch: GCalPatch, calendarId = "primary") =>
        api.patch<GCalEvent>(eventUrl(eventId, calendarId), patch),
    },

    /** Patch only extendedProperties.private, so a bad call can never alter an
     *  event's time or attendees. */
    stampPrivateProps: async (
      eventId: string,
      privateProps: Record<string, string>,
      calendarId = "primary"
    ): Promise<void> => {
      await api.patch(eventUrl(eventId, calendarId), {
        extendedProperties: { private: privateProps },
      })
    },
  }
}
